import {APIV1Version, ApiState, getClientType} from '../../common/types/common';
import {
  BatchDetailTabs,
  DeliveryLocationStatus,
  GetChatUnreadCountResponse,
  GetOrderItemByIdResponse,
  GetOrdersForOrderAssigneeResponse,
  GetRunForBatchDetailResponse,
  GetSharedItemsForShopperResponse,
  GroupedAssignedItems,
  OrderItemStatus,
  OrderItems,
  RunForBatchDetail,
  SetSubstituitonRequestParams,
  SkipPackingResponse,
  SubstitutionItemParams,
  UpdateOrderItemStatusResponse,
  itemsForSection,
  itemsSortBy,
} from '../types';
import {
  DELETE_DELIVERY_IMAGE,
  SET_DELIVERY_LOCATION_STATUS_ORDERS,
  SET_SUBSTITUTION_STATUS,
  SUBSTITUTE_ITEM_IN_RUN,
  UPDATE_ORDER_ITEM_STATUS,
} from '../../config/graphQL/documentNode/mutation';
import {
  GET_BATCH_DETAIL_RUN,
  GET_CHAT_UNREAD_COUNT,
  GET_ORDERS_FOR_ORDER_ASSIGNEE,
  GET_ORDERS_FOR_ORDER_ASSIGNEE_COUNT,
  GET_ORDER_ITEM_BY_ID_SUBSTITUTE,
  GET_SHARED_ITEMS_FOR_SHOPPER,
  GET_UPC_ORDER_ITEMS,
  GET_UPC_ORDER_ITEMS_COUNT,
} from '../../config/graphQL/documentNode/queries';
import React, {Dispatch, useCallback, useState} from 'react';
import {deepCloneObject, useCheckIfB2BStore} from '@buncha/utils/common';

import {Analytics} from '../../config/analytics/analytics';
import {AnyAction} from '@reduxjs/toolkit';
import {EventType} from '../../config/analytics/type';
import {FetchResult} from '@apollo/client';
import {ToastType} from '../../components/composites/notification/type';
import {batchDetailActionsLocal} from './batchDetail';
import {gqlService} from '../../config/graphQL';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useToastMessage} from '../../components/composites/notification';

export function useGetOrdersForOrderAssignee(
  dispatch: React.Dispatch<any> | null,
): [
  (runId: number, option: itemsForSection, sortBy?: itemsSortBy) => void,
  boolean,
  GroupedAssignedItems[],
] {
  const [loading, setLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const [itemData, setItemData] = useState<GroupedAssignedItems[]>([]);

  const setState = useCallback(
    (data: GroupedAssignedItems[], option: itemsForSection) => {
      if (dispatch)
        switch (option) {
          case itemsForSection.todo:
            dispatch(batchDetailActionsLocal.setGroupedAssignedItemsTodo(data));
            break;
          case itemsForSection.pending:
            dispatch(
              batchDetailActionsLocal.setGroupedAssignedItemsInReview(data),
            );
            break;
          case itemsForSection.done:
            dispatch(batchDetailActionsLocal.setGroupedAssignedItemsDone(data));
            break;
          case itemsForSection.all:
            dispatch(batchDetailActionsLocal.setGroupedAssignedItemsAll(data));
            break;
          default:
            return;
        }
    },
    [dispatch],
  );

  const getOrdersForOrderAssignee = useCallback(
    async (runId: number, option: itemsForSection, sortBy?: itemsSortBy) => {
      try {
        setLoading(true);
        const response =
          await gqlService?.query<GetOrdersForOrderAssigneeResponse>({
            query: GET_ORDERS_FOR_ORDER_ASSIGNEE,
            variables: {
              runId: runId,
              sortBy: sortBy,
              option: option,
            },
            fetchPolicy: 'network-only',
          });
        let items: GroupedAssignedItems[] = deepCloneObject(
          response?.data?.getOrdersForOrderAssignee ?? [],
        );

        setItemData(items);
        setState(items, option);
      } catch (error: any) {
        //use toast
        showErrorToast(error.message);
      } finally {
        setLoading(false);
      }
    },
    [setState, showErrorToast],
  );

  return [getOrdersForOrderAssignee, loading, itemData];
}

export function useGetOrdersForOrderAssigneeCount(
  dispatch: React.Dispatch<any> | null,
) {
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const {checkIfB2BStore} = useCheckIfB2BStore();

  const getOrderItemsCount = async (
    runId: number,
    option: itemsForSection,
    sortBy?: itemsSortBy,
  ) => {
    try {
      const response =
        await gqlService?.query<GetOrdersForOrderAssigneeResponse>({
          query: GET_ORDERS_FOR_ORDER_ASSIGNEE_COUNT,
          variables: {
            runId: runId,
            sortBy: sortBy,
            option: option,
          },
          fetchPolicy: 'network-only',
        });
      if (response?.data?.getOrdersForOrderAssignee && dispatch) {
        const storeId =
          response?.data?.getOrdersForOrderAssignee[0]?.metaData?.order?.run
            ?.store?.id;
        const isB2BStore = checkIfB2BStore(storeId);
        const totalEntries =
          response?.data.getOrdersForOrderAssignee[0]?.keyTotalEntries;
        Analytics.eventWithProps('getOrdersForOrderAssignee', {
          type: EventType.api,
          runID: runId,
          sortBy: sortBy,
          option: option,
          keyTotalEntries: totalEntries,
          client: getClientType(isB2BStore),
        });
        dispatch(batchDetailActionsLocal.setToDoCount(totalEntries));
      }
    } catch (error: any) {
      showErrorToast(error.message);
    }
  };

  return {getOrderItemsCount};
}

export function useGetRunForBatchDetail(
  dispatch: React.Dispatch<AnyAction> | null,
): [(runId: number) => void, ApiState<RunForBatchDetail>] {
  const B2BSTores: number[] = JSON.parse(
    useAppSelector(gState => gState.onboarding.constants?.B2B_STORE_IDS) ??
      '[]',
  );
  const [state, setState] = useState<ApiState<RunForBatchDetail>>({
    loading: true,
    error: undefined,
    data: undefined,
  });

  const getRunForBatchDetail = useCallback(
    async (runId: number) => {
      setState({...state, loading: true});
      try {
        const response = await gqlService?.query<GetRunForBatchDetailResponse>({
          query: GET_BATCH_DETAIL_RUN,
          variables: {
            runId: runId,
          },
          fetchPolicy: 'network-only',
        });
        if (response?.data?.getRun)
          setState({...state, data: response?.data.getRun, loading: false});
        dispatch?.(
          batchDetailActionsLocal.setIsB2BStore(
            Boolean(
              response?.data?.getRun?.store?.id &&
                B2BSTores?.includes(response?.data?.getRun?.store?.id),
            ),
          ),
        );
      } catch (error: any) {
        //use toast
        setState({...state, loading: false});
      }
    },
    [B2BSTores, dispatch, state],
  );

  return [getRunForBatchDetail, state];
}

export function useUpdateOrderItemStatus() {
  const [changeStatusState, setchangeStatusState] = useState<
    ApiState<OrderItems>
  >({
    loading: false,
    error: undefined,
    data: undefined,
  });
  const [showErrorToast] = useToastMessage(ToastType.Error);

  const updateOrderItemStatus = useCallback(
    async (
      runId: number,
      orderItemStatus: OrderItemStatus,
      orderItemIds: number[],
      callback?: () => void,
      dropItemMessage?: string,
      isAdjacentOosItem?: boolean,
      actualUpcs?: string[],
      // eslint-disable-next-line max-params
    ) => {
      setchangeStatusState({...changeStatusState, loading: true});
      try {
        const response =
          await gqlService?.mutation<UpdateOrderItemStatusResponse>({
            mutation: UPDATE_ORDER_ITEM_STATUS,
            variables: {
              orderItemStatus: orderItemStatus,
              orderItemIds: orderItemIds,
              dropItemMessage,
              isAdjacentOosItem,
              runId,
              actualUpcs,
            },
            fetchPolicy: 'network-only',
          });
        if (
          response?.data?.updateOrderItemStatus.itemStatus === orderItemStatus
        ) {
          setchangeStatusState({
            ...changeStatusState,
            data: response?.data.updateOrderItemStatus,
            loading: false,
          });
          if (callback) callback();
        }
      } catch (error: any) {
        showErrorToast(error?.message);
        setchangeStatusState({...changeStatusState, loading: false});
      }
    },
    [showErrorToast, changeStatusState],
  );

  return {updateOrderItemStatus, changeStatusState};
}

export function useGetOrderItemById(): [
  (orderItemId: number, runId: number) => void,
  ApiState<OrderItems>,
] {
  const [state, setState] = useState<ApiState<OrderItems>>({
    loading: false,
    error: undefined,
    data: undefined,
  });

  const getOrderItemById = useCallback(
    async (orderItemId: number, runId: number) => {
      setState({...state, loading: true});
      try {
        const response = await gqlService?.query<GetOrderItemByIdResponse>({
          query: GET_ORDER_ITEM_BY_ID_SUBSTITUTE,
          variables: {
            orderItemId: orderItemId,
            runId,
          },
          fetchPolicy: 'network-only',
        });
        if (response?.data?.getOrderItemById)
          setState({
            ...state,
            data: response?.data.getOrderItemById,
            loading: false,
          });
      } catch (error: any) {
        //use toast
        setState({...state, loading: false});
      }
    },
    [state],
  );

  return [getOrderItemById, state];
}

export function useDeleteReceiptUrl() {
  const [deletingImage, setDeletingImage] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);

  const deleteRunReceipts = async (
    url: string,
    runId: number,
    callback: Function,
  ) => {
    try {
      setDeletingImage(true);
      let res = await gqlService?.mutation({
        mutation: DELETE_DELIVERY_IMAGE,
        variables: {
          runId: runId,
          receiptUrl: url,
        },
        fetchPolicy: 'network-only',
      });
      if (res?.data?.deleteRunreceipt) callback();
      Analytics.eventWithProps('Delete shopper receipt', {
        type: EventType.cta,
        runId: runId,
      });
    } catch (error: any) {
      showErrorToast(error.message);
    } finally {
      setDeletingImage(false);
    }
  };

  return {deletingImage, deleteRunReceipts};
}

export function useSetSubstitutionStatus() {
  const [loading, setLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const setSubstitutionStatus = async (
    params: SetSubstituitonRequestParams,
  ) => {
    setLoading(true);
    try {
      await gqlService?.mutation({
        mutation: SET_SUBSTITUTION_STATUS,
        variables: params,
        fetchPolicy: 'network-only',
      });
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      showErrorToast(err?.message);
    }
  };

  return {setSubstitutionStatus, loading};
}

export function useSubstituteItemInRun() {
  const [loading, setLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const substituteItemInRun = async (params: SubstitutionItemParams) => {
    try {
      setLoading(true);
      await gqlService?.mutation({
        mutation: SUBSTITUTE_ITEM_IN_RUN,
        fetchPolicy: 'network-only',
        variables: params,
      });
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      showErrorToast(err?.message);
    }
  };

  return {substituteItemInRun, loading};
}

export function useUPCShift(dispatch: Dispatch<any> | null) {
  const [orderLoading, setOrderLoading] = useState<boolean>(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);

  const fetchUPCOrderItems = async (
    upcOrderId: number,
    option: BatchDetailTabs,
    fetchAll: boolean,
  ) => {
    try {
      setOrderLoading(true);
      const variables = {
        upcOrderId,
        option,
      };
      let res: FetchResult<any> | undefined;

      if (fetchAll)
        res = await gqlService?.query({
          query: GET_UPC_ORDER_ITEMS_COUNT,
          fetchPolicy: 'network-only',
          variables,
        });
      else
        res = await gqlService?.query({
          query: GET_UPC_ORDER_ITEMS,
          fetchPolicy: 'network-only',
          variables,
        });

      if (dispatch) {
        if (option === BatchDetailTabs.done)
          dispatch(
            batchDetailActionsLocal.setUPCItemsDone(
              res?.data?.getUpcOrder?.orderItems?.length,
            ),
          );
        else
          dispatch(
            batchDetailActionsLocal.setUPCItemsTodo(
              res?.data?.getUpcOrder?.orderItems?.length,
            ),
          );
        dispatch(
          batchDetailActionsLocal.setUPCOrderData(res?.data?.getUpcOrder),
        );
      }
    } catch (error: any) {
      showErrorToast(error.message);
    } finally {
      setOrderLoading(false);
    }
  };

  const getUPCShiftOrder = async (
    upcOrderId: number,
    option: BatchDetailTabs,
    fetchAll: boolean,
  ) => {
    if (fetchAll)
      try {
        await Promise.all([
          fetchUPCOrderItems(upcOrderId, BatchDetailTabs.todo, fetchAll),
          fetchUPCOrderItems(upcOrderId, BatchDetailTabs.done, fetchAll),
        ]);
      } catch (error: any) {
        showErrorToast(error.message);
      }
    else
      try {
        await fetchUPCOrderItems(upcOrderId, option, fetchAll);
      } catch (error: any) {
        showErrorToast(error.message);
      }
  };

  return {getUPCShiftOrder, orderLoading};
}

export function useGetChatUnreadCount() {
  const [chatLoading, setChatLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);

  const getChatUnreadCount = async (conversationId: string[]) => {
    try {
      setChatLoading(true);
      const res = await gqlService?.query<GetChatUnreadCountResponse>({
        query: GET_CHAT_UNREAD_COUNT,
        variables: {
          conversationId,
        },
        fetchPolicy: 'network-only',
      });
      return res?.data?.getChatUnreadCount?.data?.count;
    } catch (err: any) {
      showErrorToast(err?.message);
    } finally {
      setChatLoading(false);
    }
  };

  return {getChatUnreadCount, chatLoading};
}

export function useSkipPacking() {
  const [skipPackingLoading, setSkipPackingLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);

  const handleSkipPacking = async (
    runId: number,
    status: DeliveryLocationStatus,
    orderIds: number[],
  ) => {
    setSkipPackingLoading(true);
    try {
      const res = await gqlService?.mutation<SkipPackingResponse>({
        mutation: SET_DELIVERY_LOCATION_STATUS_ORDERS,
        fetchPolicy: 'network-only',
        variables: {
          runId,
          orderIds,
          skipPacking: true,
          status,
        },
      });
      return res?.data?.setDeliveryLocationStatusOfAssignedOrders.id;
    } catch (err: any) {
      showErrorToast(err?.message);
    } finally {
      setSkipPackingLoading(false);
    }
  };

  return {handleSkipPacking, skipPackingLoading};
}

export function useAdjacentOOSItems(dispatch: React.Dispatch<any> | null) {
  const [loading, setLoading] = useState(false);
  const [showErrorToast] = useToastMessage(ToastType.Error);

  const getAdjacentOOSItems = async (
    type: 'locate' | 'sent',
    runId: number,
  ) => {
    setLoading(true);
    try {
      const res = await gqlService?.query<GetSharedItemsForShopperResponse>({
        query: GET_SHARED_ITEMS_FOR_SHOPPER,
        variables: {
          version: APIV1Version.V100,
          requestParams: {
            runId,
            type,
          },
        },
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'graphql-version': 'v1',
          },
        },
      });
      const items =
        res?.data?.getSharedItemsForShopper?.data?.V100?.sharedItems ?? [];
      if (type === 'locate')
        dispatch?.(
          batchDetailActionsLocal.setGroupedAssignedItemsLocate(items),
        );
      else
        dispatch?.(batchDetailActionsLocal.setGroupedAssignedItemsSent(items));
      return items;
    } catch (err: any) {
      showErrorToast(err?.message);
    } finally {
      setLoading(false);
    }
  };

  return {getAdjacentOOSItems, loading};
}
